(() => {
  'use strict';

  const ROOT_SELECTOR = '#BroderikFreeGift';
  const ERROR_STORAGE_KEY = 'broderik_free_gift_discount_error_until';

  let root = null;
  let config = null;
  let syncing = false;
  let syncAgain = false;
  let syncTimer = null;
  let postCartUpdateTimers = [];
  let suppressSyncUntil = 0;
  let cartInteractionVersion = 0;
  let reloading = false;

  function getRootUrl() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return '/';
  }

  function cartUrl(endpoint) {
    return `${getRootUrl()}${endpoint}`;
  }

  function formatMoney(value) {
    const amount = Number(value) / 100;

    try {
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: config.currency || 'JPY',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    } catch (error) {
      return `¥${Math.round(amount).toLocaleString('ja-JP')}`;
    }
  }

  async function parseResponse(response) {
    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      // Ignore JSON parsing error and use generic error below.
    }

    if (!response.ok) {
      const message =
        data?.description ||
        data?.message ||
        `Cart request failed (${response.status})`;

      throw new Error(message);
    }

    return data;
  }

  async function getCart() {
    const response = await fetch(cartUrl('cart.js'), {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    return parseResponse(response);
  }

  async function cartWrite(endpoint, payload) {
    const response = await fetch(cartUrl(endpoint), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });

    return parseResponse(response);
  }

  function isGiftItem(item) {
    return Number(item.variant_id) === config.giftVariantId;
  }

  function getGiftItems(cart) {
    return cart.items.filter(isGiftItem);
  }

  function getQualifyingSubtotal(cart) {
    return cart.items.reduce((subtotal, item) => {
      if (isGiftItem(item)) {
        return subtotal;
      }

      /*
       * final_line_price includes line-level discounts.
       * Sale pricing created using Shopify Price / Compare-at Price
       * is already represented by the actual selling price.
       */
      return subtotal + Number(item.final_line_price || 0);
    }, 0);
  }

  function giftIsActuallyFree(giftItems) {
    if (!giftItems.length) {
      return false;
    }

    return giftItems.every(
      item => Number(item.final_line_price || 0) === 0
    );
  }

  function giftQuantity(giftItems) {
    return giftItems.reduce(
      (quantity, item) => quantity + Number(item.quantity || 0),
      0
    );
  }

  async function addGift() {
    return cartWrite('cart/add.js', {
      items: [
        {
          id: config.giftVariantId,
          quantity: 1,
          properties: {
            _broderik_free_gift: 'true'
          }
        }
      ]
    });
  }

  async function changeLine(key, quantity) {
    return cartWrite('cart/change.js', {
      id: key,
      quantity
    });
  }

  async function removeAllGiftLines() {
    /*
     * Re-fetch after each deletion because Shopify line-item keys
     * can change when discounts or cart characteristics change.
     */
    for (let attempt = 0; attempt < 5; attempt += 1) {
      const cart = await getCart();
      const giftItems = getGiftItems(cart);

      if (!giftItems.length) {
        return cart;
      }

      await changeLine(giftItems[0].key, 0);
    }

    return getCart();
  }

  async function normalizeGift(giftItems) {
    /*
     * Normal state:
     * exactly one gift line with quantity 1.
     */

    if (
      giftItems.length === 1 &&
      Number(giftItems[0].quantity) !== 1
    ) {
      await changeLine(giftItems[0].key, 1);
      return;
    }

    if (giftItems.length > 1) {
      await removeAllGiftLines();
      await addGift();
    }
  }

  function getHeadlineElement() {
    return root.querySelector('[data-bgfg-headline]');
  }

  function getSubtextElement() {
    return root.querySelector('[data-bgfg-subtext]');
  }

  function getProgressElement() {
    return root.querySelector('[data-bgfg-progress]');
  }

  function getProgressFillElement() {
    return root.querySelector('[data-bgfg-progress-fill]');
  }

  function getErrorElement() {
    return root.querySelector('[data-bgfg-error]');
  }

  function setProgress(percent) {
    const progress = Math.max(0, Math.min(100, percent));
    const progressElement = getProgressElement();
    const fill = getProgressFillElement();

    if (fill) {
      fill.style.width = `${progress}%`;
    }

    if (progressElement) {
      progressElement.setAttribute(
        'aria-valuenow',
        String(Math.round(progress))
      );
    }
  }

  function hideError() {
    const error = getErrorElement();

    if (!error) return;

    error.hidden = true;
    error.textContent = '';
  }

  function renderProgress(subtotal) {
    hideError();

    const remaining = Math.max(
      0,
      config.threshold - subtotal
    );

    const progress =
      config.threshold > 0
        ? (subtotal / config.threshold) * 100
        : 0;

    root.dataset.state = 'progress';

    getHeadlineElement().textContent =
      `無料ギフトまであと ${formatMoney(remaining)}`;

    getSubtextElement().textContent =
      '小計10,000円以上で1,000円相当の無料ギフト';

    setProgress(progress);
  }

  function renderLoading() {
    hideError();

    root.dataset.state = 'loading';

    getHeadlineElement().textContent =
      '無料ギフトを追加しています…';

    getSubtextElement().textContent =
      '小計10,000円以上で1,000円相当の無料ギフト';

    setProgress(100);
  }

  function renderSuccess() {
    hideError();

    root.dataset.state = 'success';

    getHeadlineElement().textContent =
      '無料ギフトを追加しました';

    getSubtextElement().textContent =
      '1,000円相当の無料ギフトがカートに入っています';

    setProgress(100);
  }

  function renderError(message) {
    root.dataset.state = 'error';

    getHeadlineElement().textContent =
      '無料ギフトを適用できませんでした';

    getSubtextElement().textContent =
      'カートを更新してもう一度お試しください';

    const error = getErrorElement();

    if (error) {
      error.hidden = false;
      error.textContent = message || '';
    }
  }

  function discountErrorIsCoolingDown() {
    try {
      const until = Number(
        sessionStorage.getItem(ERROR_STORAGE_KEY) || 0
      );

      return until > Date.now();
    } catch (error) {
      return false;
    }
  }

  function markDiscountError() {
    try {
      /*
       * Prevent an add → discover it isn't free → remove →
       * reload → add loop if the automatic discount has
       * been configured incorrectly.
       */
      sessionStorage.setItem(
        ERROR_STORAGE_KEY,
        String(Date.now() + 60000)
      );
    } catch (error) {
      // sessionStorage can be unavailable in unusual browser modes.
    }
  }

  function clearDiscountError() {
    try {
      sessionStorage.removeItem(ERROR_STORAGE_KEY);
    } catch (error) {
      // Ignore.
    }
  }

  function refreshRoot() {
    const liveRoot = document.querySelector(ROOT_SELECTOR);

    if (liveRoot) {
      root = liveRoot;
    }

    return root;
  }

  function getSuppressionWait() {
    return Math.max(0, suppressSyncUntil - Date.now());
  }

  function syncIsSuppressed() {
    return getSuppressionWait() > 0;
  }

  function cancelPostCartUpdateChecks() {
    postCartUpdateTimers.forEach(timer => {
      window.clearTimeout(timer);
    });

    postCartUpdateTimers = [];
    window.clearTimeout(syncTimer);
  }

  function beginNativeCartOperation(waitMs = 3000) {
    /*
     * A Turbo remove/delete action is about to mutate the cart.
     * Cancel any delayed gift checks created by an earlier +/- click,
     * invalidate any in-flight read, and temporarily let Turbo own the cart.
     */
    cartInteractionVersion += 1;
    syncAgain = false;
    cancelPostCartUpdateChecks();

    suppressSyncUntil = Math.max(
      suppressSyncUntil,
      Date.now() + waitMs
    );

    /*
     * If Turbo updates by AJAX without a full page reload, do one clean
     * reconciliation after its operation has had time to finish.
     */
    scheduleSync(waitMs + 100);
  }

  function canMutateCart(syncVersion) {
    return (
      syncVersion === cartInteractionVersion &&
      !syncIsSuppressed() &&
      !reloading
    );
  }

  function reloadCartPage() {
    if (reloading) return;

    reloading = true;
    cancelPostCartUpdateChecks();
    window.location.reload();
  }

  async function syncGift() {
    refreshRoot();

    if (!root || !config || reloading) return;

    /*
     * Never start gift cart writes while Turbo is in the middle of a
     * native remove/delete operation.
     */
    if (syncIsSuppressed()) {
      scheduleSync(100);
      return;
    }

    if (syncing) {
      syncAgain = true;
      return;
    }

    syncing = true;
    const syncVersion = cartInteractionVersion;

    try {
      let cart = await getCart();

      /*
       * A remove click may have happened while /cart.js was loading.
       * If so, discard this stale result and wait for Turbo to finish.
       */
      if (!canMutateCart(syncVersion)) {
        scheduleSync(100);
        return;
      }

      const subtotal = getQualifyingSubtotal(cart);
      const qualified = subtotal >= config.threshold;
      let giftItems = getGiftItems(cart);

      /*
       * NOT QUALIFIED
       * Remove the gift if paid merchandise has dropped below threshold.
       */
      if (!qualified) {
        clearDiscountError();

        if (giftItems.length) {
          if (!canMutateCart(syncVersion)) {
            scheduleSync(100);
            return;
          }

          await removeAllGiftLines();

          if (!canMutateCart(syncVersion)) {
            scheduleSync(100);
            return;
          }

          reloadCartPage();
          return;
        }

        refreshRoot();

        if (root) {
          renderProgress(subtotal);
        }

        return;
      }

      /*
       * QUALIFIED - no gift yet.
       */
      if (!giftItems.length) {
        if (discountErrorIsCoolingDown()) {
          refreshRoot();

          if (root) {
            renderError(
              '自動割引の設定をご確認ください。'
            );
          }

          return;
        }

        refreshRoot();

        if (root) {
          renderLoading();
        }

        if (!canMutateCart(syncVersion)) {
          scheduleSync(100);
          return;
        }

        await addGift();

        if (!canMutateCart(syncVersion)) {
          scheduleSync(100);
          return;
        }

        /*
         * Re-fetch because Shopify calculates the automatic 100%-off
         * gift-product discount after the gift line has been added.
         */
        cart = await getCart();

        if (!canMutateCart(syncVersion)) {
          scheduleSync(100);
          return;
        }

        giftItems = getGiftItems(cart);

        if (!giftItems.length) {
          throw new Error(
            'The free gift could not be added. Check gift inventory and variant ID.'
          );
        }

        /*
         * Safety check: never intentionally leave the gift in the cart
         * at its normal ¥1,000 price.
         */
        if (!giftIsActuallyFree(giftItems)) {
          markDiscountError();

          if (!canMutateCart(syncVersion)) {
            scheduleSync(100);
            return;
          }

          await removeAllGiftLines();

          if (!canMutateCart(syncVersion)) {
            scheduleSync(100);
            return;
          }

          reloadCartPage();
          return;
        }

        reloadCartPage();
        return;
      }

      /*
       * Keep the gift at exactly one unit.
       */
      if (
        giftItems.length !== 1 ||
        giftQuantity(giftItems) !== 1
      ) {
        if (!canMutateCart(syncVersion)) {
          scheduleSync(100);
          return;
        }

        await normalizeGift(giftItems);

        if (!canMutateCart(syncVersion)) {
          scheduleSync(100);
          return;
        }

        reloadCartPage();
        return;
      }

      /*
       * Safety check for an existing gift.
       */
      if (!giftIsActuallyFree(giftItems)) {
        markDiscountError();

        if (!canMutateCart(syncVersion)) {
          scheduleSync(100);
          return;
        }

        await removeAllGiftLines();

        if (!canMutateCart(syncVersion)) {
          scheduleSync(100);
          return;
        }

        reloadCartPage();
        return;
      }

      clearDiscountError();
      refreshRoot();

      if (root) {
        renderSuccess();
      }

    } catch (error) {
      console.error(
        '[Broderik Free Gift]',
        error
      );

      refreshRoot();

      if (root && !reloading) {
        renderError(error.message);
      }

    } finally {
      syncing = false;

      if (syncAgain && !reloading) {
        syncAgain = false;
        scheduleSync(100);
      }
    }
  }

  function scheduleSync(delay = 300) {
    if (reloading) return;

    window.clearTimeout(syncTimer);

    const suppressionWait = getSuppressionWait();
    const effectiveDelay = Math.max(delay, suppressionWait + 50);

    syncTimer = window.setTimeout(() => {
      syncGift();
    }, effectiveDelay);
  }

  function schedulePostCartUpdateChecks() {
    /*
     * Turbo desktop can finish its AJAX quantity request after the click
     * event. Re-check a few times instead of assuming one fixed delay.
     */
    postCartUpdateTimers.forEach(timer => {
      window.clearTimeout(timer);
    });

    postCartUpdateTimers = [];

    const delays = [350, 800, 1400, 2200];

    delays.forEach(delay => {
      const timer = window.setTimeout(() => {
        scheduleSync(0);
      }, delay);

      postCartUpdateTimers.push(timer);
    });
  }

  function installCartEventListeners() {
    /*
     * Shopify's standardized storefront cart-line event.
     * This also keeps the module portable to Horizon later.
     */
    document.addEventListener(
      'shopify:cart:lines-update',
      () => {
        clearDiscountError();
        scheduleSync(150);
      }
    );

    /*
     * Direct quantity-input changes.
     */
    document.addEventListener(
      'change',
      event => {
        const target = event.target;

        if (!(target instanceof Element)) return;

        const quantityInput = target.matches(
          '[data-cart-quantity-input="cart-template"], input.quantity[name="updates[]"]'
        );

        if (!quantityInput) return;

        clearDiscountError();
        schedulePostCartUpdateChecks();
      }
    );

    /*
     * Cart clicks.
     *
     * IMPORTANT:
     * We only react to Turbo's +/- controls. We deliberately do not run
     * gift synchronization for every generic click inside the cart form.
     */
    document.addEventListener(
      'click',
      event => {
        const target = event.target;

        if (!(target instanceof Element)) return;

        /*
         * Checkout buttons are unrelated to gift synchronization.
         */
        if (
          target.closest(
            '[name="checkout"], .additional-checkout-buttons'
          )
        ) {
          return;
        }

        /*
         * Normal Turbo remove button, warranty remove button, or any cart
         * change URL whose quantity is explicitly zero.
         *
         * Cancel all delayed gift checks and give Turbo temporary exclusive
         * control of the cart. Do not preventDefault() and do not mutate the
         * cart from this handler.
         */
        const removeButton = target.closest(
          '.cart__remove-btn, [data-remove-item="cart-template"], .broderik-warranty-remove, a[href*="quantity=0"]'
        );

        if (removeButton) {
          beginNativeCartOperation(3000);
          return;
        }

        /*
         * Turbo +/- quantity controls.
         */
        const quantityButton = target.closest(
          '.js-change-quantity'
        );

        if (quantityButton) {
          clearDiscountError();
          schedulePostCartUpdateChecks();
        }
      },
      true
    );

    /*
     * Fallback for Turbo replacing/updating cart markup by AJAX.
     * Ignore mutations made inside our own progress widget.
     */
    const cartContainer =
      document.querySelector('form[action*="/cart"]');

    if (
      cartContainer &&
      'MutationObserver' in window
    ) {
      const observer = new MutationObserver(
        mutations => {
          refreshRoot();

          const hasExternalMutation =
            mutations.some(mutation => {
              if (!(mutation.target instanceof Node)) {
                return true;
              }

              if (!root) {
                return true;
              }

              return !root.contains(mutation.target);
            });

          if (hasExternalMutation) {
            scheduleSync(500);
          }
        }
      );

      observer.observe(cartContainer, {
        childList: true,
        subtree: true
      });
    }
  }

  function init() {
    root = document.querySelector(ROOT_SELECTOR);

    /*
     * The assets can safely be loaded globally.
     * Nothing runs on pages without the widget.
     */
    if (!root) return;

    const giftVariantId = Number(
      root.dataset.giftVariantId
    );

    const threshold = Number(
      root.dataset.threshold
    );

    const giftValue = Number(
      root.dataset.giftValue
    );

    if (
      !Number.isInteger(giftVariantId) ||
      giftVariantId <= 0 ||
      !Number.isFinite(threshold) ||
      threshold <= 0
    ) {
      console.error(
        '[Broderik Free Gift] Invalid configuration.'
      );

      return;
    }

    config = {
      giftVariantId,
      threshold,
      giftValue,
      currency: root.dataset.currency || 'JPY'
    };

    installCartEventListeners();
    syncGift();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();